import Vue from 'vue'
import ApiService from '@/components/Checkout/Payment/ApplePay/_shared/api-service'
import ROUTE_NAMES from '@/router/route-names.js'

export const CHECKOUT_STEPS = [
  ROUTE_NAMES.checkoutRegistration,
  ROUTE_NAMES.checkoutShipping,
  ROUTE_NAMES.checkoutPayment,
]

const getStepIndex = step => CHECKOUT_STEPS.indexOf(step)
export const isStepPresent = step => getStepIndex(step) >= 0
export const getNextStep = step => CHECKOUT_STEPS[getStepIndex(step) + 1]
export const getPreviousStep = step => CHECKOUT_STEPS[getStepIndex(step) - 1]
export const isConfirmation = step => step === ROUTE_NAMES.checkoutConfirmation

export default {
  namespaced: true,
  state: {
    wantsNewsletter: false,
    shippingAddress: null,
    billingAddress: null,
    accountAddressId: {
      shipping: null,
      billing: null,
    },
    errorCount: {
      shipping: 0,
      billing: 0,
      total: 0,
    },
    paths: {
      authorizeMerchantsUrl: null,
      termsAndConditionsUrl: null,
      updateCheckoutAdressUrl: null,
      updateCheckoutDeliveryUrl: null,
      updateCheckoutRegistrationUrl: null,
    },
    loggedIn: false,
    savedPayments: [],
    paymentPublicKey: null,
    generationTime: null,
    adyenAuthorizePaymentUrl: null,
    isApplePayUser: false,
    applePay: {
      authorizePaymentUrl: null,
      isAvailable: false,
      checkoutRequest: null,
      expressCheckoutRequest: null,
    },
  },

  getters: {
    wantsNewsletter: state => state.wantsNewsletter,
    loggedIn: state => state.loggedIn,
    termsAndConditionsUrl: state => state.paths.termsAndConditionsUrl,
    savedPayments: state =>
      state.savedPayments.sort(card => Date.parse(card.creationDate)).reverse(),
    paymentPublicKey: state => state.paymentPublicKey,
    generationTime: state => state.generationTime,
    adyenAuthorizePaymentUrl: state => state.adyenAuthorizePaymentUrl,
    authorizeMerchantsUrl: state => state.paths.authorizeMerchantsUrl,
    updateCheckoutAdressUrl: state => state.paths.updateCheckoutAdressUrl,
    updateCheckoutDeliveryUrl: state => state.paths.updateCheckoutDeliveryUrl,
    updateCheckoutRegistrationUrl: state => state.paths.updateCheckoutRegistrationUrl,
    applePayAuthorizePaymentUrl: state => state.applePay.authorizePaymentUrl,
    applePayCheckoutRequest: state => state.applePay.checkoutRequest,
    applePayExpressCheckoutRequest: state => state.applePay.expressCheckoutRequest,
    showApplePayButton: state => state.isApplePayUser && state.applePay.isAvailable,
    latestAccessibleStep: (state, getters) =>
      CHECKOUT_STEPS.slice()
        .reverse() // search from last to first
        .find(step => getters.isStepAccessible(step)),
    isStepAccessible: (state, getters) => step =>
      // confirmation page is not considered a checkout step, but should always be accessible
      isConfirmation(step) ||
      (isStepPresent(step) &&
        getters.isPreviousStepValid(step) &&
        !(step === ROUTE_NAMES.checkoutRegistration && getters.isStepValid(step))),
    isPreviousStepValid: (state, getters) => step => {
      const previousStep = getPreviousStep(step)
      // undefined previous steps are considered valid
      return previousStep ? getters.isStepValid(previousStep) : true
    },
    isStepValid: (state, getters, rootState) => step => {
      const { isRegistrationValid, isShippingValid, isPaymentValid } = rootState.order
      switch (step) {
        case ROUTE_NAMES.checkoutRegistration:
          return isRegistrationValid
        case ROUTE_NAMES.checkoutShipping:
          return isRegistrationValid && isShippingValid
        case ROUTE_NAMES.checkoutPayment:
          return isRegistrationValid && isShippingValid && isPaymentValid
        default:
          return false
      }
    },
  },

  mutations: {
    setState(state, payload) {
      for (let key in payload) {
        state[key] = payload[key]
      }
    },

    setShippingAddress(state, addressObject) {
      state.shippingAddress = { ...state.shippingAddress, ...addressObject }
    },

    setBillingAddress(state, addressObject) {
      state.billingAddress = { ...state.billingAddress, ...addressObject }
    },

    resetShippingAddress(state, addressObject) {
      state.shippingAddress = { ...addressObject }
    },

    resetBillingAddress(state, addressObject) {
      state.billingAddress = { ...addressObject }
    },

    setAccountAddressId(state, payload) {
      Vue.set(state.accountAddressId, payload[0], payload[1])
    },

    setErrorCount(state, errorCountObject) {
      state.errorCount = { ...state.errorCount, ...errorCountObject }
    },

    setApplePayUser(state, enabled) {
      Vue.set(state, 'isApplePayUser', enabled)
    },
  },

  actions: {
    updateErrorCount({ commit, state }, payload) {
      const otherKey = payload.key == 'shipping' ? 'billing' : 'shipping'
      const newErrorCount = {}
      newErrorCount[payload.key] = payload.value
      newErrorCount.total = payload.value + state.errorCount[otherKey]

      commit('setErrorCount', newErrorCount)
    },

    updateApplePayExpressCheckoutRequest({ commit, state }, payload = {}) {
      const { onLoaded } = payload

      new ApiService().fetchApplePayExpressCheckout().then(({ checkout, paths }) => {
        const newState = { ...state, ...checkout, paths }
        commit('setState', newState)
        onLoaded && onLoaded()
      })
    },
  },
}
